/**
 * App shell (ui-architecture.html): top bar, app rail, module sidebar with the V1 menu nested
 * at the bottom, drafts tray and the ⌘K palette. Screens render into <Outlet />.
 */
import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { api, useApi, useEvents } from "../lib/api";
import { ago } from "../lib/format";
import { RANGES, useRange } from "../lib/range";
import { useSession } from "../lib/session";
import { MODULES, moduleFor, V1_GROUPS } from "../nav";
import { Copilot } from "./Copilot";
import { DateRange } from "./DateRange";
import { Icon } from "./Icon";
import { Palette } from "./Palette";
import "./Shell.css";

interface Tray {
  count: number;
  setIds: string[];
  summary: string;
}

export type NavCounts = Record<string, number | undefined> & {
  dataAsOf?: string | null;
  /** the Google Ads account's time zone (IANA) */
  timeZone?: string;
};

export function Shell() {
  const { me, can } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const { days, query, custom, setDays } = useRange();
  const mod = moduleFor(location.pathname);
  const [navOpen, setNavOpen] = useState(false);
  const [palette, setPalette] = useState(false);
  const [theme, setTheme] = useState(() => document.documentElement.getAttribute("data-theme") ?? "");
  const tray = useApi<Tray>(can("analyst") ? "/drafts/current" : null);
  const counts = useApi<NavCounts>("/nav-counts");

  useEvents(["changeset.updated", "inbox.new", "sync.finished"], () => {
    tray.reload();
    counts.reload();
  });
  useEffect(() => {
    const h = () => {
      tray.reload();
      counts.reload();
    };
    window.addEventListener("drafts:changed", h);
    // counts also catch up when the tab comes back into view and once a minute
    const seen = () => document.visibilityState === "visible" && h();
    document.addEventListener("visibilitychange", seen);
    const t = setInterval(seen, 60_000);
    return () => {
      window.removeEventListener("drafts:changed", h);
      document.removeEventListener("visibilitychange", seen);
      clearInterval(t);
    };
  }, [tray.reload, counts.reload]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPalette(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: close the mobile drawer on navigation
  useEffect(() => {
    setNavOpen(false);
    setMeOpen(false);
  }, [location.pathname]);

  const toggleTheme = () => {
    const cur = theme || (matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light");
    const next = cur === "dark" ? "light" : "dark";
    document.documentElement.setAttribute("data-theme", next);
    try {
      localStorage.setItem("brecx-theme", next);
    } catch {
      /* private mode: theme just isn't remembered */
    }
    setTheme(next);
  };

  // the account menu closes on a click elsewhere, on Escape and on navigation
  const [meOpen, setMeOpen] = useState(false);
  const meRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!meOpen) return;
    const away = (e: MouseEvent) => {
      if (!meRef.current?.contains(e.target as Node)) setMeOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMeOpen(false);
    };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", esc);
    };
  }, [meOpen]);

  const logout = async () => {
    await api.post("/auth/logout");
    navigate("/login");
  };

  const discard = async () => {
    for (const id of tray.data?.setIds ?? []) await api.post(`/change-sets/${id}/discard`);
    tray.reload();
    counts.reload();
    window.dispatchEvent(new Event("drafts:changed"));
  };

  const withRange = (to: string) => `${to}?${query}`;
  const v1 = me.v1Url?.replace(/\/$/, "");
  const onV1 = location.pathname === "/v1" || location.pathname.startsWith("/v1/");
  const initials = (me.user.name ?? me.user.email)
    .split(/\s+/)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="app">
      <header className="top">
        <button
          type="button"
          className="menu-btn"
          aria-label="Open navigation"
          aria-expanded={navOpen}
          onClick={() => setNavOpen((o) => !o)}
        >
          <Icon name="menu" />
        </button>
        <Link to={withRange("/")} className="brand">
          <svg width="26" height="26" viewBox="0 0 26 26" aria-hidden="true">
            <rect width="26" height="26" rx="7" fill="var(--text)" />
            <rect x="6" y="14" width="3" height="6" rx="1" fill="var(--surface)" />
            <rect x="11.5" y="10" width="3" height="10" rx="1" fill="var(--surface)" />
            <rect x="17" y="6" width="3" height="14" rx="1" fill="var(--s3)" />
          </svg>
          <div>
            <b>Brecx Ads</b>
            <small>ads · catalog · social</small>
          </div>
        </Link>
        <span className="acct">
          <span className="av">{me.workspace.name.slice(0, 1)}</span>
          <span className="nm">{me.workspace.name}</span>
        </span>
        <div className="sp" />
        <button type="button" className="search-btn" onClick={() => setPalette(true)}>
          <Icon name="search" />
          <span>Search or jump to</span>
          <kbd className="kbd">⌘K</kbd>
        </button>
        <div className="seg range-seg" role="group" aria-label="Date range">
          {RANGES.map((d) => (
            <button
              type="button"
              key={d}
              className={!custom && d === days ? "on" : ""}
              aria-pressed={!custom && d === days}
              onClick={() => setDays(d)}
            >
              {d}d
            </button>
          ))}
        </div>
        <DateRange timeZone={counts.data?.timeZone} compact />
        <div
          className="fresh"
          title={
            counts.data?.dataAsOf
              ? `Metrics synced ${new Date(counts.data.dataAsOf).toLocaleString()}`
              : "No sync has finished yet"
          }
        >
          <span className={`pulse${counts.data?.dataAsOf ? "" : " warn"}`} />
          <span className="lbl-t">
            {counts.data?.dataAsOf ? `Synced ${ago(counts.data.dataAsOf)}` : "Not synced yet"}
          </span>
        </div>
        <Link
          to={withRange("/settings")}
          className={`chip-safe${me.workspace.safeMode ? "" : " off"}`}
          title={
            me.workspace.safeModeForced
              ? "Safe mode is forced by the server (SAFE_MODE_FORCE): approved changes are only validated with Google. Nothing is written."
              : me.workspace.safeMode
                ? "Safe mode: changes are only validated with Google (validateOnly). Nothing is written."
                : "Safe mode is off: approved changes are written to Google Ads."
          }
        >
          <Icon name="shield" />
          <span className="t">
            {me.workspace.safeModeForced
              ? "Safe mode · server"
              : me.workspace.safeMode
                ? "Safe mode"
                : "Live writes"}
          </span>
        </Link>
        <Copilot />
        <button type="button" className="icon-btn" aria-label="Toggle theme" onClick={toggleTheme}>
          <Icon name={theme === "dark" ? "sun" : "moon"} />
        </button>
        <Link to={withRange("/inbox")} className="icon-btn" aria-label="Suggestions">
          <Icon name="bell" />
          {counts.data?.inbox ? <span className="dot" /> : null}
        </Link>
        {/* the account button opens a small menu; signing out is a choice in it, not the click itself */}
        <div className="me-wrap" ref={meRef}>
          <button
            type="button"
            className="me"
            title={`${me.user.email} · ${me.role}`}
            aria-haspopup="menu"
            aria-expanded={meOpen}
            aria-label="Account"
            onClick={() => setMeOpen((x) => !x)}
          >
            {initials}
          </button>
          {meOpen ? (
            <div className="me-menu" role="menu">
              <div className="me-who">
                <b>{me.user.name || me.user.email}</b>
                <span className="muted">{me.user.email}</span>
                <span className="muted">
                  {me.workspace.name} · {me.role}
                </span>
              </div>
              <Link to="/settings" role="menuitem" className="me-item" onClick={() => setMeOpen(false)}>
                <Icon name="gear" size={14} />
                Settings
              </Link>
              <button type="button" role="menuitem" className="me-item" onClick={() => void logout()}>
                <Icon name="undo" size={14} />
                Sign out
              </button>
            </div>
          ) : null}
        </div>
      </header>

      <aside className="rail" aria-label="Apps">
        {MODULES.map((m) => (
          <Link
            key={m.key}
            to={withRange(m.groups[0]!.items[0]!.to)}
            className={m.key === mod.key ? "on" : ""}
            title={m.label}
          >
            <Icon name={m.railIcon} size={18} />
            <span>{m.railLabel}</span>
          </Link>
        ))}
        <Link
          to={withRange("/apps")}
          title="Apps: every tool on one page"
          className={location.pathname === "/apps" ? "on" : ""}
        >
          <Icon name="grid" size={18} />
          <span>Apps</span>
        </Link>
        <div className="rail-sp" />
        {v1 ? (
          <Link to="/v1/" title="The v1 app" className={onV1 ? "on" : ""}>
            <Icon name="undo" size={18} />
            <span>V1</span>
          </Link>
        ) : null}
      </aside>

      <nav className={`nav${navOpen ? " open" : ""}`} aria-label="Main">
        <div className="nav-mod">
          <div className="mod-h">
            <b>{mod.label}</b>
            <span>{me.workspace.name}</span>
          </div>
          {mod.groups.map((g, gi) => (
            <div className="nav-g" key={g.heading ?? gi}>
              {g.heading ? <div className="nav-h">{g.heading}</div> : null}
              {g.items.map((i) => {
                const n = i.count ? counts.data?.[i.count] : undefined;
                return (
                  <NavLink
                    key={i.to}
                    to={withRange(i.to)}
                    end={i.to === "/"}
                    className={({ isActive }) => (isActive ? "on" : "")}
                  >
                    <Icon name={i.icon} />
                    {i.label}
                    {n ? (
                      <span className={`cnt${i.hot ? " hot" : ""}`}>
                        {n > 999 ? `${(n / 1000).toFixed(1)}k` : n}
                      </span>
                    ) : null}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </div>

        {/* opens by itself on a v1 page; otherwise starts closed */}
        <details className="nav-v1" key={onV1 ? "v1-on" : "v1"} open={onV1 || undefined}>
          <summary>
            <span>V1</span>
            <span className="chip plain">classic</span>
            <Icon name="chev" className="nav-chev" />
          </summary>
          {v1 ? (
            V1_GROUPS.map((g, gi) => (
              <div className="nav-g" key={g.heading ?? gi}>
                {g.heading ? <div className="nav-h">{g.heading}</div> : null}
                {g.items.map((i) => (
                  <NavLink
                    key={i.path}
                    to={`/v1${i.path}`}
                    end={i.path === "/"}
                    className={({ isActive }) => (isActive ? "on" : "")}
                  >
                    <Icon name={i.icon} />
                    {i.label}
                  </NavLink>
                ))}
              </div>
            ))
          ) : (
            <p className="nav-note">Set V1_URL on the API to link the classic app here.</p>
          )}
        </details>
      </nav>

      <main className={`main${tray.data && tray.data.count > 0 ? " has-tray" : ""}`} id="main">
        <Outlet />
      </main>

      {tray.data && tray.data.count > 0 ? (
        <div className="tray" role="region" aria-label="Draft changes">
          <div className="in">
            <span className="n">{tray.data.count}</span>
            <div>
              <b>Draft changes</b>{" "}
              <span className="muted" id="trayS">
                {tray.data.summary}
              </span>
            </div>
            <button type="button" className="btn sm" onClick={discard}>
              Discard
            </button>
            <Link className="btn sm pri" to={`/changes?set=${tray.data.setIds[0]}&${query}`}>
              Review &amp; publish
            </Link>
          </div>
        </div>
      ) : null}

      {palette ? <Palette onClose={() => setPalette(false)} v1Url={v1 ?? null} rangeQuery={query} /> : null}
    </div>
  );
}
